import React, { FC, ReactNode } from 'react';
import { CampaignBlock, SlotSize } from './CampaignBlock';

/**
 * Props for ShowstopperBlock component
 */
export interface ShowstopperBlockProps {
    /** The component(s) to render when condition is true */
    children: ReactNode;
    /** Boolean condition to determine if children should be rendered */
    condition: boolean;
    /** Array of slot sizes to use for fallback CampaignBlock when condition is false */
    fallbackLayout?: SlotSize[];
}

/**
 * ShowstopperBlock - A wrapper component that prevents layout shifts when components are conditionally hidden.
 *
 * ## Problem It Solves
 * When carousels or other components are conditionally rendered (e.g., based on experiments, user state, etc.),
 * hiding them causes all subsequent components to shift positions. This creates a poor user experience and
 * makes it difficult to maintain consistent campaign positioning across different user segments.
 *
 * ## How It Works
 * ShowstopperBlock acts as a conditional wrapper that:
 * 1. When `condition` is `true`: Renders the children (the carousel/widget)
 * 2. When `condition` is `false`: Renders CampaignBlock(s) as fallback to maintain layout spacing
 *
 * This ensures that the space is always occupied, preventing position shifts of other carousels.
 *
 * ## When to Use
 * **REQUIRED** for all non-CampaignBlock components in OneColumnLayout and TwoColumnLayout:
 * - Carousels, Widgets and any component that might be conditionally shown/hidden
 *
 * **NOT NEEDED** for:
 * - CampaignBlock components (already part of the grid system)
 * - Components at the very end of the layout that don't affect positioning
 * - ThreeColumnLayout and FourColumnLayout (different layout strategy)
 *
 * ## Usage Examples
 *
 * ### One Column Layout (Mobile)
 * ```tsx
 * <ShowstopperBlock condition={true} fallbackLayout={[Slot.MOBILE]}>
 *   <ThisFeatureComponent />
 * </ShowstopperBlock>
 * ```
 *
 * ### Two Column Layout (Tablet)
 * ```tsx
 * <ShowstopperBlock condition={true} fallbackLayout={[Slot.HORIZONTAL_RECTANGLE]}>
 *   <ThisFeatureComponent {...props} />
 * </ShowstopperBlock>
 * ```
 *
 * ### With Conditional Logic
 * ```tsx
 * {isThisFeatureTrue && (
 *   <ShowstopperBlock condition={shouldShowDeals} fallbackLayout={[Slot.MOBILE]}>
 *     <ThisFeatureComponent {...props} />
 *   </ShowstopperBlock>
 * )}
 * ```
 *
 * ## Important Notes
 * - Only used in OneColumnLayout and TwoColumnLayout
 * - The `fallbackLayout` should match the approximate space the component would occupy
 * - Always wrap new carousels/widgets with ShowstopperBlock to prevent position shifts
 * - During code review, verify that all non-CampaignBlock components are properly wrapped
 *
 * @param props - ShowstopperBlockProps
 * @returns The children if condition is true, otherwise a CampaignBlock fallback
 *
 * @example
 * // Basic usage in OneColumnLayout
 * <ShowstopperBlock condition={true} fallbackLayout={[Slot.MOBILE]}>
 *   <MyCarousel />
 * </ShowstopperBlock>
 *
 * @example
 * // With experiment flag in TwoColumnLayout
 * const showExperimentalFeature = useExperiment('new-carousel-test');
 * <ShowstopperBlock condition={showExperimentalFeature} fallbackLayout={[Slot.HORIZONTAL_RECTANGLE]}>
 *   <ExperimentalCarousel />
 * </ShowstopperBlock>
 */
export const ShowstopperBlock: FC<ShowstopperBlockProps> = ({
    children,
    condition,
    fallbackLayout,
}) => {
    if (!condition && !fallbackLayout) return null;
    if (condition) return children;

    return <CampaignBlock slots={fallbackLayout} />;
};
